/**
 * Spec 014 Phase 3: the brand's cancellation rules on `/admin/brand-settings`.
 * Plain functions (form values, validation, the changed fields, an example),
 * tested in `cancellationRulesAdmin.test.ts`. The server checks the same
 * limits (`brand-rules.validation.ts`) and computes every refund
 * (`brand-rules/cancellation-rules.ts`).
 */

export type LateFeeMode = 'flat' | 'perDay' | 'percentOfTotal';
export type ShortenMode =
  | 'percentOfRemaining'
  | 'perDayCapped'
  | 'flatCapped'
  | 'noRefundStartedPeriod';

export type LateCancellationFee =
  | { mode: 'flat'; amount: number }
  | { mode: 'perDay'; amount: number }
  | { mode: 'percentOfTotal'; percent: number };

export type ShortenRule =
  | { mode: 'percentOfRemaining'; percent: number }
  | { mode: 'perDayCapped'; amount: number }
  | { mode: 'flatCapped'; amount: number }
  | { mode: 'noRefundStartedPeriod' };

export interface CancellationRules {
  processingFeePercent: number;
  stackLateFeeWithProcessingFee: boolean;
  gracePeriodHours: number;
  freeCancellationAdvanceHours: number;
  lateCancellationFee: LateCancellationFee;
  shortenRule: ShortenRule;
}

export type CancellationRuleKey = keyof CancellationRules;

export const CANCELLATION_RULE_KEYS: readonly CancellationRuleKey[] = [
  'processingFeePercent',
  'stackLateFeeWithProcessingFee',
  'gracePeriodHours',
  'freeCancellationAdvanceHours',
  'lateCancellationFee',
  'shortenRule',
];

/** The platform defaults (`PLATFORM_BOOKING_RULES` on the server). */
export const DEFAULT_CANCELLATION_RULES: CancellationRules = {
  processingFeePercent: 0,
  stackLateFeeWithProcessingFee: false,
  gracePeriodHours: 1,
  freeCancellationAdvanceHours: 24,
  lateCancellationFee: { mode: 'flat', amount: 50 },
  shortenRule: { mode: 'percentOfRemaining', percent: 50 },
};

export const LATE_FEE_MODES: ReadonlyArray<{ value: LateFeeMode; label: string; unit: string }> = [
  { value: 'flat', label: 'Flat amount', unit: '$' },
  { value: 'perDay', label: 'Per trip day', unit: '$ per day' },
  { value: 'percentOfTotal', label: 'Percent of the trip total', unit: '%' },
];

export const SHORTEN_MODES: ReadonlyArray<{ value: ShortenMode; label: string; unit: string }> = [
  { value: 'percentOfRemaining', label: "Percent of the removed days' price", unit: '%' },
  { value: 'perDayCapped', label: 'Per removed day', unit: '$ per day' },
  { value: 'flatCapped', label: 'Flat amount', unit: '$' },
  { value: 'noRefundStartedPeriod', label: 'No refund for removed days', unit: '' },
];

/** The rule titles, also used for the change history. */
export const CANCELLATION_RULE_TITLES: Record<CancellationRuleKey, string> = {
  processingFeePercent: 'Processing fee',
  stackLateFeeWithProcessingFee: 'Processing fee on late cancellations',
  gracePeriodHours: 'Grace period after booking',
  freeCancellationAdvanceHours: 'Free cancellation deadline',
  lateCancellationFee: 'Late cancellation fee',
  shortenRule: 'Shortening penalty',
};

/** What the inputs hold; numbers stay strings while the admin types. */
export interface CancellationRulesForm {
  processingFeePercent: string;
  stackLateFeeWithProcessingFee: boolean;
  gracePeriodHours: string;
  freeCancellationAdvanceHours: string;
  lateFeeMode: LateFeeMode;
  lateFeeValue: string;
  shortenMode: ShortenMode;
  shortenValue: string;
}

function isObject(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** The brand's `bookingRules` (JSON from `brandRules`) as cancellation rules, defaults for gaps. */
export function cancellationRulesOf(bookingRules: unknown): CancellationRules {
  const stored = isObject(bookingRules) ? bookingRules : {};
  const rules = { ...DEFAULT_CANCELLATION_RULES };
  for (const key of CANCELLATION_RULE_KEYS) {
    if (stored[key] !== undefined && stored[key] !== null) {
      (rules as Record<string, unknown>)[key] = stored[key];
    }
  }
  return rules;
}

function lateFeeValue(fee: LateCancellationFee): number {
  return fee.mode === 'percentOfTotal' ? fee.percent : fee.amount;
}

function shortenValue(rule: ShortenRule): number | null {
  if (rule.mode === 'noRefundStartedPeriod') return null;
  return rule.mode === 'percentOfRemaining' ? rule.percent : rule.amount;
}

export function toForm(rules: CancellationRules): CancellationRulesForm {
  const shorten = shortenValue(rules.shortenRule);
  return {
    processingFeePercent: String(rules.processingFeePercent),
    stackLateFeeWithProcessingFee: rules.stackLateFeeWithProcessingFee,
    gracePeriodHours: String(rules.gracePeriodHours),
    freeCancellationAdvanceHours: String(rules.freeCancellationAdvanceHours),
    lateFeeMode: rules.lateCancellationFee.mode,
    lateFeeValue: String(lateFeeValue(rules.lateCancellationFee)),
    shortenMode: rules.shortenRule.mode,
    shortenValue: shorten === null ? '' : String(shorten),
  };
}

export type FormErrors = Partial<Record<keyof CancellationRulesForm, string>>;

const MAX_MONEY = 100000;
const MAX_HOURS = 24 * 365;

function readNumber(
  raw: string,
  max: number,
  errorKey: keyof CancellationRulesForm,
  errors: FormErrors,
): number {
  const text = raw.trim();
  const value = Number(text);
  if (text === '' || !Number.isFinite(value)) {
    errors[errorKey] = 'Enter a number';
  } else if (value < 0) {
    errors[errorKey] = 'Cannot be negative';
  } else if (value > max) {
    errors[errorKey] = `At most ${max}`;
  }
  return value;
}

/** The form as rules, or the problems to show next to the inputs. */
export function fromForm(
  form: CancellationRulesForm,
): { rules: CancellationRules; errors: null } | { rules: null; errors: FormErrors } {
  const errors: FormErrors = {};
  const processingFeePercent = readNumber(
    form.processingFeePercent,
    100,
    'processingFeePercent',
    errors,
  );
  const gracePeriodHours = readNumber(form.gracePeriodHours, MAX_HOURS, 'gracePeriodHours', errors);
  const freeCancellationAdvanceHours = readNumber(
    form.freeCancellationAdvanceHours,
    MAX_HOURS,
    'freeCancellationAdvanceHours',
    errors,
  );
  const lateValue = readNumber(
    form.lateFeeValue,
    form.lateFeeMode === 'percentOfTotal' ? 100 : MAX_MONEY,
    'lateFeeValue',
    errors,
  );
  const shortenNumber =
    form.shortenMode === 'noRefundStartedPeriod'
      ? 0
      : readNumber(
          form.shortenValue,
          form.shortenMode === 'percentOfRemaining' ? 100 : MAX_MONEY,
          'shortenValue',
          errors,
        );
  if (Object.keys(errors).length > 0) {
    return { rules: null, errors };
  }

  const lateCancellationFee: LateCancellationFee =
    form.lateFeeMode === 'percentOfTotal'
      ? { mode: 'percentOfTotal', percent: lateValue }
      : { mode: form.lateFeeMode, amount: lateValue };
  let shortenRule: ShortenRule;
  switch (form.shortenMode) {
    case 'noRefundStartedPeriod':
      shortenRule = { mode: 'noRefundStartedPeriod' };
      break;
    case 'percentOfRemaining':
      shortenRule = { mode: 'percentOfRemaining', percent: shortenNumber };
      break;
    default:
      shortenRule = { mode: form.shortenMode, amount: shortenNumber };
  }
  return {
    rules: {
      processingFeePercent,
      stackLateFeeWithProcessingFee: form.stackLateFeeWithProcessingFee,
      gracePeriodHours,
      freeCancellationAdvanceHours,
      lateCancellationFee,
      shortenRule,
    },
    errors: null,
  };
}

/** Only the rules that differ from what was loaded; the server replaces whole fields. */
export function changedCancellationRules(
  loaded: CancellationRules,
  edited: CancellationRules,
): Partial<CancellationRules> {
  return CANCELLATION_RULE_KEYS.reduce<Partial<CancellationRules>>((acc, key) => {
    if (JSON.stringify(loaded[key]) !== JSON.stringify(edited[key])) {
      (acc as Record<string, unknown>)[key] = edited[key];
    }
    return acc;
  }, {});
}

function money(value: number): string {
  return `$${(Math.round(value * 100) / 100).toFixed(2)}`;
}

/** A rule value in words, for the history list. */
export function describeCancellationValue(key: string, value: unknown): string | null {
  if (!(CANCELLATION_RULE_KEYS as readonly string[]).includes(key)) return null;
  if (value === null || value === undefined) return 'not set';
  switch (key as CancellationRuleKey) {
    case 'processingFeePercent':
      return `${value}%`;
    case 'stackLateFeeWithProcessingFee':
      return value ? 'on' : 'off';
    case 'gracePeriodHours':
      return Number(value) === 0 ? 'none' : `${value} h`;
    case 'freeCancellationAdvanceHours':
      return `${value} h before pickup`;
    case 'lateCancellationFee': {
      const fee = value as LateCancellationFee;
      if (fee.mode === 'percentOfTotal') return `${fee.percent}% of the trip total`;
      return fee.mode === 'perDay' ? `${money(fee.amount)} per trip day` : money(fee.amount);
    }
    case 'shortenRule': {
      const rule = value as ShortenRule;
      if (rule.mode === 'noRefundStartedPeriod') return 'no refund for removed days';
      if (rule.mode === 'percentOfRemaining') return `${rule.percent}% of the removed days`;
      return rule.mode === 'perDayCapped'
        ? `${money(rule.amount)} per removed day`
        : `${money(rule.amount)} flat`;
    }
    default:
      return null;
  }
}

/**
 * Refunds for one example trip under `rules`, the same math as the server, so
 * the admin sees what a change does before saving.
 */
export function exampleRefunds(
  rules: CancellationRules,
  trip: { totalPrice: number; totalDays: number } = { totalPrice: 700, totalDays: 7 },
): { early: string; late: string } {
  const fee = (trip.totalPrice * rules.processingFeePercent) / 100;
  const late = rules.lateCancellationFee;
  const lateFee = Math.min(
    late.mode === 'percentOfTotal'
      ? (trip.totalPrice * late.percent) / 100
      : late.mode === 'perDay'
        ? late.amount * trip.totalDays
        : late.amount,
    trip.totalPrice,
  );
  const stacked = rules.stackLateFeeWithProcessingFee
    ? Math.min(fee, trip.totalPrice - lateFee)
    : 0;
  const deadline = rules.freeCancellationAdvanceHours;
  return {
    early: `Cancelled more than ${deadline} h before pickup: refund ${money(trip.totalPrice - fee)} of ${money(trip.totalPrice)}.`,
    late: `Cancelled within ${deadline} h of pickup: refund ${money(trip.totalPrice - lateFee - stacked)} of ${money(trip.totalPrice)}.`,
  };
}
